import {createFeatureSelector, createSelector} from '@ngrx/store';
import * as _ from 'lodash';

import {LookupState, TemplateLookup, TimecardTemplateLookup} from '../reducers/lookup.reducer';
import {PayCode, PayType1, PayType2Map} from '../../../common/pay-code.model';
import {WorkLocationViewModel} from '../../../common/start-card.model';
import {Department} from '../../../common/department-setting.model';
import {FullUnion, JobDetails, Schedule} from '../../../common/contract-rate.model';
import {TimecardTemplate} from '../../../common/template.model';
import {GLShortcutsViewModel} from '../../../common/project-setting.model';
import {Paymaster} from '../../../common/paymaster.model';
import {IWorkStatusCombinations} from '../../../common/work-status.model';
import {DayType, PayType, TimecardLookupData} from '../../../common/timecard.model';
import {ExportReportOption} from '../../../common/report.model';
import {selectRerateJobCodeValue} from '../daily-time/selectors/sag/daily-time.selector';
import {selectUserDepartments} from '../auth/selectors';
import {TimecardTypes} from '../../../common/timecard.enum';
import {ProjectServiceRegion} from '../../../common/project.model';
import {Unit} from '../../../common/unit.model';
import {sortUnits} from '../shared/unit.service';

const _selectLookupRootState = createFeatureSelector<LookupState>('lookup');
export const selectPayType2Map = createSelector(
  _selectLookupRootState,
  (state: LookupState): PayType2Map => state && state.payType2Map
);
export const selectBasePayOptions = createSelector(
  selectPayType2Map,
  (state: PayType2Map): PayCode[] => state ? state['BASE PAY'] : []
);

// Returns a filtered payType2Map that excludes pay codes marked as isBreakageGridOnly: true.
export const selectSummaryPayType2Map = createSelector(
  selectPayType2Map,
  (payType2Map: PayType2Map): PayType2Map => {
    if (!payType2Map) { return {}; }
    return _.mapValues(payType2Map, (payCodes: PayCode[]) =>
      payCodes.filter((payCode: PayCode) => !payCode.isBreakageGridOnly)
    );
  }
);

export const selectAllowanceOptions = createSelector(
  selectPayType2Map,
  (state: PayType2Map): PayCode[] => _.sortBy(state ? state['ALLOWANCE'] : [], 'payType2')
);
export const selectAdditionalsOptions = createSelector(
  selectPayType2Map,
  (state: PayType2Map): PayCode[] => _.sortBy(state ? state['ADDITIONALS'] : [], [
    (pc: PayCode) => pc.payType2.replace(/\s*-\s*[\d.]+X$/i, '').trim(),
    (pc: PayCode) => pc.paycodeFactor ?? 0
  ])
);

export const selectOthersOptions = createSelector(
  selectPayType2Map,
  (state: PayType2Map): PayCode[] => _.sortBy(state ? state['OTHER'] : [], 'payType2')
);

export const selectDeductionsOptions = createSelector(
  selectPayType2Map,
  (state: PayType2Map): PayCode[] => _.sortBy(state ? state['DEDUCTIONS'] : [], 'payType2')
);

export const selectHolidayOptions = createSelector(
  selectPayType2Map,
  (state: PayType2Map): PayCode[] => state ? state['HOLIDAY'] : []
);
export const selectTaxJurisdiction = createSelector(
  _selectLookupRootState,
  (state): WorkLocationViewModel[] => state && state.taxJurisdiction
);

export const selectDepartments = createSelector(
  _selectLookupRootState,
  (state: LookupState): Department[] => state && state.departmentList
);

export const selectFirstDepartmentId = createSelector(
  selectDepartments,
  (state: Department[]): string => _.head(state).id ?? ''
);

export const selectUnionCodeDetails = createSelector(
  _selectLookupRootState,
  (lookup: LookupState ): FullUnion[] => lookup ? lookup.unions : []
);

export const selectPayType1List = createSelector(
  _selectLookupRootState,
  (state: LookupState): PayType1[] => state && state.payType1List
);

export const selectPayType2List = createSelector(
  _selectLookupRootState,
  (state: LookupState): PayType1[] => state && state.payType2List
);

// UK Requirement: Filters out pay codes marked as isBreakageGridOnly: true from the full payType2List.
// Breakage-only pay codes are exclusively for the Breakage section and must not appear in Summary or other sections.
export const selectSummaryPayType2List = createSelector(
  selectPayType2List,
  (payType2List: PayType1[]): PayType1[] => payType2List?.filter((payCode: PayCode) => !payCode.isBreakageGridOnly) ?? []
);

export const selectPayCodeByPayCodeNumber = (paycodeNumber: string) =>
  createSelector(
  _selectLookupRootState,
  (state: LookupState): PayCode => (state && _.find(state.payType2List, {paycodeNumber: paycodeNumber}))
    ?? null
);

// UK Requirement: In UK timecards, certain pay codes are designated exclusively for the Breakage section
// (indicated by isBreakageGridOnly: true). These pay codes must NOT appear in the timecard Summary section.
export const selectTimecardSummaryPayCodeByPayCodeNumber = (paycodeNumber: string) => createSelector(
  selectPayCodeByPayCodeNumber(paycodeNumber),
  (payCode: PayCode): PayCode => (payCode && !payCode.isBreakageGridOnly) ? payCode : null
);

export const selectGlShortcutList = createSelector(
  _selectLookupRootState,
  (state: LookupState): GLShortcutsViewModel[] => state ? state.glShortcutList : []
);

export const selectTemplateLookup = createSelector(
  _selectLookupRootState,
  (state: LookupState): TemplateLookup => state ? state.template : null
);

export const selectTimecardTemplateLookupState = createSelector(
  selectTemplateLookup,
  (state: TemplateLookup): TimecardTemplateLookup => state ? state.timecardTemplate : null
);

export const selectTimecardTemplateLookup = createSelector(
  selectTimecardTemplateLookupState,
  (state: TimecardTemplateLookup): TimecardTemplate => state && state.timecard
);

export const selectServiceRegionsLookup = createSelector(
  _selectLookupRootState,
  (state: LookupState): ProjectServiceRegion[] => state && state.serviceRegions
);

export const selectPayManagerLookUp = createSelector(
  _selectLookupRootState,
  (state: LookupState): Paymaster[] => state ? state.paymasterManagers : []
);

export const selectPayMasterLookUp = createSelector(
  _selectLookupRootState,
  (state: LookupState): Paymaster[] => state ? state.paymasters : []
);

export const selectWorkStatusCombination = createSelector(
  _selectLookupRootState,
  (state: LookupState): IWorkStatusCombinations => state && state.workStatusCombinations
);

export const selectTimecardLookupData = createSelector(
  _selectLookupRootState,
  (state: LookupState): {[key in keyof typeof TimecardTypes]?: TimecardLookupData} => state && state.timecardLookupData
);

export const selectSagPayTypeLookupData = createSelector(
  selectTimecardLookupData,
  (state: { [key in keyof typeof TimecardTypes]?: TimecardLookupData }): PayType[] | DayType[] => _.get(state, `SAG.payType`, [])
);
export const selectSagDayTypeLookupData = (workType: string) => createSelector(
  selectTimecardLookupData,
  (state: { [key in keyof typeof TimecardTypes]?: TimecardLookupData }): DayType[] => _.get(state, `SAG.${workType}`, [])
);

export const selectCrewPayTypeLookupData = createSelector(
  selectTimecardLookupData,
  (state: { [key in keyof typeof TimecardTypes]?: TimecardLookupData }): PayType[] => _.get(state, `CREW.payType`, [])
);
export const selectCrewDayType1LookupData = createSelector(
  selectTimecardLookupData,
  (state: { [key in keyof typeof TimecardTypes]?: TimecardLookupData }): DayType[] => _.get(state, `CREW.dayType1`, [])
);
export const selectCrewDayType2LookupData = createSelector(
  selectTimecardLookupData,
  (state: { [key in keyof typeof TimecardTypes]?: TimecardLookupData }): DayType[] => _.get(state, `CREW.dayType2`, [])
);

export const selectStartcardExportReportOptions = createSelector(
    _selectLookupRootState,
    (state: LookupState): ExportReportOption[] => state && state.startcardExportReportOptions
);

export const selectPayrollDepartmentExportReportOptions = createSelector(
    _selectLookupRootState,
    (state: LookupState): ExportReportOption[] => state && state.payrollDistributionExportReportOptions
);

export const selectJobDetails = (contractProfileId: string) => createSelector(
  _selectLookupRootState,
  (state: LookupState): JobDetails[] => state.jobDetails?.[contractProfileId] || []
);

export const selectScheduleDetails = (contractProfileId: string, startcardId: string) => createSelector(
  _selectLookupRootState,
  selectRerateJobCodeValue(startcardId),
  (state: LookupState, code: string): Schedule[] => {
    // Combine all the schedules from the jobDetails that matches the user selection. Because, in SAG union's the jobCodes are similar but multiple jobDetails
    // are present. So since we are not persisting the jobId, the schedules in other job details are not visible to user.
    return _
      .chain(state.jobDetails?.[contractProfileId] || [])
      .filter({code})
      .map((jobDetail: JobDetails) => jobDetail.schedules ?? [])
      .flatten()
      .uniqWith(_.isEqual)
      .value();
  }
);

// Todo: Need to move this selector to auth, will do in https://epglobal.atlassian.net/browse/STF-26495
// If we moved this selector to auth, after that some existing client test is not working, so we should find new solution and root cause of this issue.
export const selectHasAllDepartmentAccess = createSelector(
  selectDepartments,
  selectUserDepartments,
  (projectDepartments: Department[], userDepartments: Department[]): boolean => _.isEqual(projectDepartments, userDepartments)
);

export const selectUnitDetails = createSelector(
  _selectLookupRootState,
  (lookup: LookupState): Unit[] => {
    const activeUnits = _.filter(lookup?.units ?? [], 'active');
    return sortUnits(activeUnits);
  }
);
